import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import PageSeo from "@/components/site/PageSeo";
import { useSettings, usePageCopy } from "@/contexts/SettingsContext";
import { useSiteContact } from "@/lib/siteContact";
import { trackLeadConversion } from "@/lib/seo";

const ContactPage = () => {
    const { pages = {}, settings } = useSettings() || {};
    const contact = useSiteContact();
    const copy = usePageCopy("contact", { title: "Contact us" });
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", consent: false });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const validate = () => {
        if (!form.name.trim()) return "Please enter your name";
        if (!form.email.trim()) return "Please enter your email";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email";
        if (!form.subject.trim()) return "Please enter a subject";
        if (!form.message.trim()) return "Please enter your message";
        if (!form.consent) return "Please accept the consent checkbox";
        return null;
    };

    const submit = async (e) => {
        e.preventDefault();
        const err = validate();
        if (err) { toast.error(err); return; }
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            trackLeadConversion(settings);
            setSuccess(true);
            toast.success("Message sent. We'll reply within 24 hours");
            setForm({ name: "", email: "", subject: "", message: "", consent: false });
        } catch {
            toast.error("Could not send message. Please try again.");
        } finally { setSubmitting(false); }
    };

    const field = (id, label, control) => (
        <div>
            <label className="block text-[13px] font-semibold text-[#0A2540] mb-1.5" htmlFor={id}>
                {label} <span className="text-[#E31837]">*</span>
            </label>
            {control}
        </div>
    );

    return (
        <div className="bg-[#F4F6F8]" data-testid="contact-page">
            <PageSeo pageKey="contact" />
            <div className="bg-[#0A2540] text-white">
                <div className="container-x py-8 md:py-10" data-testid="contact-header">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#E31837] flex items-center justify-center shrink-0">
                            <Mail size={20} />
                        </div>
                        <p className="text-xs uppercase tracking-widest text-white/60">{copy.eyebrow || "Get in touch"}</p>
                    </div>
                    <h1 className="font-display font-bold text-[1.65rem] leading-[1.15] sm:text-3xl mt-3 max-w-3xl" style={{ textWrap: "balance" }}>
                        {copy.title}
                    </h1>
                    {copy.subtitle ? (
                        <p className="text-sm leading-relaxed text-white/75 mt-2 max-w-2xl" style={{ textWrap: "pretty" }}>
                            {copy.subtitle}
                        </p>
                    ) : null}
                </div>
            </div>

            <section className="container-x py-6 md:py-8">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6 md:items-stretch">
                    <div className="md:col-span-3 h-full">
                        {success ? (
                            <div className="h-full rounded-2xl border border-[#e8eaee] bg-white p-5 sm:p-6 flex flex-col items-start justify-center" data-testid="contact-success">
                                <CheckCircle2 size={28} className="text-emerald-500" />
                                <h2 className="font-display font-bold text-lg text-[#0A2540] mt-3">Message received</h2>
                                <p className="text-sm text-[#7a8597] mt-1 max-w-md">Thank you. We will reply within 24 hours on the email you provided.</p>
                                <button type="button" onClick={() => setSuccess(false)} className="btn btn-white mt-5 min-h-[44px]" data-testid="contact-send-another">Send another message</button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="h-full rounded-2xl border border-[#e8eaee] bg-white p-4 sm:p-6" data-testid="contact-form">
                                <h2 className="font-display font-bold text-lg text-[#0A2540]">Send a message</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                                    {field("contact-name", "Name", (
                                        <input id="contact-name" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" placeholder="Your full name" data-testid="contact-name" required autoComplete="name" />
                                    ))}
                                    {field("contact-email-input", "Email", (
                                        <input id="contact-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" placeholder="you@example.com" data-testid="contact-email-input" required autoComplete="email" inputMode="email" />
                                    ))}
                                </div>
                                <div className="mt-3">
                                    {field("contact-subject", "Subject", (
                                        <input id="contact-subject" type="text" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="input" placeholder="What is this about?" data-testid="contact-subject" required />
                                    ))}
                                </div>
                                <div className="mt-3">
                                    {field("contact-message", "Message", (
                                        <textarea id="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input min-h-[120px] resize-y" rows={4} placeholder="Tell us how we can help" data-testid="contact-message" required />
                                    ))}
                                </div>
                                <label className="flex items-start gap-3 mt-4 text-[13px] leading-relaxed text-[#4a4a52] cursor-pointer">
                                    <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5 h-5 w-5 shrink-0 accent-[#E31837]" data-testid="contact-consent" />
                                    <span>I agree to be contacted by Servall about my inquiry and confirm I have read the <Link to="/privacy-policy" className="text-[#E31837] font-semibold underline underline-offset-2">Privacy Policy</Link>.</span>
                                </label>
                                <button type="submit" disabled={submitting} className="btn btn-primary w-full justify-center mt-4 min-h-[44px]" data-testid="contact-submit">
                                    {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                                    {submitting ? "Sending..." : "Send message"}
                                </button>
                            </form>
                        )}
                    </div>

                    <div className="md:col-span-2 md:h-full">
                        <div className="h-full flex flex-col rounded-2xl border border-[#e8eaee] bg-white p-4 sm:p-6">
                            <h2 className="font-display font-bold text-lg text-[#0A2540]">Reach us</h2>
                            <div className="mt-4 space-y-3 text-sm">
                                <a href={`mailto:${contact.email}`} className="flex items-start gap-3 min-h-[44px] rounded-xl hover:bg-[#F4F6F8] -mx-2 px-2 py-2" data-testid="contact-email">
                                    <Mail size={16} className="shrink-0 mt-0.5 text-[#E31837]" />
                                    <span className="min-w-0">
                                        <span className="block text-[13px] text-[#7a8597]">Email</span>
                                        <span className="block font-semibold text-[#0A2540] break-all">{contact.email}</span>
                                    </span>
                                </a>
                                <a href={contact.phoneTel} className="flex items-start gap-3 min-h-[44px] rounded-xl hover:bg-[#F4F6F8] -mx-2 px-2 py-2" data-testid="contact-phone">
                                    <Phone size={16} className="shrink-0 mt-0.5 text-[#E31837]" />
                                    <span className="min-w-0">
                                        <span className="block text-[13px] text-[#7a8597]">Phone</span>
                                        <span className="block font-semibold text-[#0A2540]">{contact.phoneDisplay}</span>
                                    </span>
                                </a>
                                <div className="flex items-start gap-3 px-0 py-1">
                                    <MapPin size={16} className="shrink-0 mt-0.5 text-[#E31837]" />
                                    <span className="min-w-0">
                                        <span className="block text-[13px] text-[#7a8597]">{pages.contact_office_label || "Head office"}</span>
                                        <span className="block font-semibold text-[#0A2540]">{pages.contact_office_address || "Servall Automotive Pvt Ltd, Gujarat, India"}</span>
                                    </span>
                                </div>
                            </div>
                            <div className="mt-auto pt-5 space-y-2">
                                <a
                                    href={contact.whatsappUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full min-h-[44px] flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ea952] text-white font-semibold text-sm"
                                    data-testid="contact-whatsapp-hero"
                                >
                                    <MessageCircle size={16} /> WhatsApp
                                </a>
                                <a href={contact.phoneTel} className="btn btn-white w-full justify-center min-h-[44px]">
                                    <Phone size={16} /> Call
                                </a>
                                <Link to="/dealers" className="flex items-center justify-center gap-1 min-h-[44px] text-sm font-semibold text-[#E31837]">
                                    <MapPin size={14} /> Find an outlet
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ContactPage;
